import { useState, useEffect } from "react";
import Panel from "../conponents/common/Panel";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  BagIcon, // 袋子圖標
  WaterIcon, // 水圖標
  SettingIcon, // 設定圖標
  BoltIcon, // 閃電圖標
  Menu, // 選單圖標
} from "../conponents/setting/constants/icons";
import DeviceTypeSelector from "../conponents/setting/DeviceTypeSelector";
import DragPreview from "../conponents/setting/DragPreview";
import EditDeviceModal from "../conponents/setting/EditDeviceModal";

import SortableTreeNode from "../conponents/setting/SortableTreeNode";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchDeviceList,
  updateDeviceList,
  setCurrentMode,
  selectCurrentList,
  selectCurrentMode,
  selectCurrentCategory,
} from "../store/settingSlice";
import { toast } from "react-toastify";

export default function Setting() {
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [pendingParentId, setPendingParentId] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const dispatch = useDispatch();
  const currentList = useSelector(selectCurrentList);
  const currentMode = useSelector(selectCurrentMode);
  const currentCategory = useSelector(selectCurrentCategory);
  const [treeData, setTreeData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLocalChange, setIsLocalChange] = useState(false); // 標記是否為本地變更
  const [collapsedNodes, setCollapsedNodes] = useState([]);

  const handleUpdate = () => {
    if (!isDataLoaded || !isLocalChange) return; // 只在本地變更時才更新

    dispatch(
      updateDeviceList({
        list: treeData,
        mode: currentMode,
      })
    ).then((res) => {
      // 更新成功後重置本地變更標記
      if (res.payload.error === 0) {
        setIsLocalChange(false);
        toast.success(res.payload.msg);
      } else {
        toast.error(res.payload.msg);
      }
    });
  };

  // 初始化時獲取數據
  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 當 currentList 從 Redux 變化時同步到本地 treeData
  useEffect(() => {
    if (currentList && Array.isArray(currentList)) {
      setTreeData(currentList);
      setIsDataLoaded(true);
      setIsLocalChange(false); // 這是來自 Redux 的變更，不是本地變更
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentList]);

  // 當 treeData 本地變更時觸發更新
  useEffect(() => {
    if (isDataLoaded && isLocalChange) {
      console.log("Local treeData changed, updating...", treeData);
      // 使用 setTimeout 避免同步更新問題
      const timeoutId = setTimeout(() => {
        handleUpdate();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, isDataLoaded, isLocalChange]);

  // 修改 handleDeviceTypeSelect 函數
  const handleDeviceTypeSelect = (deviceType) => {
    const newNode = createNewNode(deviceType);

    if (pendingParentId) {
      // 添加為子節點
      const newTreeData = addToChildren(treeData, pendingParentId, newNode);
      setTreeData(newTreeData);
      setIsLocalChange(true); // 🔑 標記為本地變更
    } else {
      console.warn("無法添加到根級別，所有節點必須在預設根節點底下");
    }

    setShowDeviceSelector(false);
    setPendingParentId(null);
  };

  // 修改 handleSaveEdit 函數
  const handleSaveEdit = (nodeId, formData) => {
    const updateTree = (nodes) => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            name: formData.name,
            fieldsValues: {
              architecture: formData.architecture,
              endDevice: formData.endDevice,
              voltage: formData.voltage,
              ampere: formData.ampere,
              phases: formData.phases,
            },
          };
        }
        if (node.children) {
          return { ...node, children: updateTree(node.children) };
        }
        return node;
      });
    };

    const newTreeData = updateTree(treeData);
    setTreeData(newTreeData);
    setIsLocalChange(true); // 🔑 標記為本地變更
    setShowEditModal(false);
    setEditingNode(null);
  };

  // 修改 deleteNode 函數
  const deleteNode = (nodeId) => {
    // 檢查是否為第一層節點，如果是則阻止刪除
    if (isFirstLevel(nodeId)) {
      console.warn("第一層節點無法刪除");
      return;
    }

    const nodeInfo = findNodePath(treeData, nodeId);
    if (nodeInfo) {
      const newTreeData = removeNodeByPath(treeData, nodeInfo.path);
      setTreeData(newTreeData);
      setIsLocalChange(true); // 🔑 標記為本地變更
    }
  };

  const toggleExpand = (nodeId) => {
    setCollapsedNodes((prev) => {
      if (prev.includes(nodeId)) {
        return prev.filter((id) => id !== nodeId); // 移除 = 展開
      } else {
        return [...prev, nodeId]; // 加入 = 收起
      }
    });
  };
  const isNodeExpanded = (nodeId) => {
    return !collapsedNodes.includes(nodeId);
  };

  // 修改 handleDragEnd 函數中設置 treeData 的部分
  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);
    setDraggedNode(null);

    if (!over || active.id === over.id) {
      return;
    }

    // 檢查是否嘗試拖拽第一層節點
    if (isFirstLevel(active.id)) {
      console.warn("第一層節點無法被移動");
      return;
    }

    console.log("Drag end:", { activeId: active.id, overId: over.id });

    // 處理設備類型拖拽（從上方拖拽新增）
    if (isDeviceTypeId(active.id)) {
      const deviceType = active.data.current?.deviceType;
      if (!deviceType) return;

      const newNode = createNewNode(deviceType);

      // 設備類型只能添加為子節點，不允許添加到根層級
      if (over.id === "root") {
        console.warn("無法將節點移動到根層級，所有節點必須在預設根節點底下");
        return;
      } else {
        const newTreeData = addToChildren(treeData, over.id, newNode);
        setTreeData(newTreeData);
        setIsLocalChange(true); // 🔑 標記為本地變更
      }
      return;
    }

    // 处理普通节点拖拽
    const activeNodeInfo = findNodeWithParent(treeData, active.id);
    if (!activeNodeInfo) {
      console.log("Active node not found");
      return;
    }

    // 如果目標是根層級，直接拒絕
    if (over.id === "root") {
      console.warn("無法將節點移動到根層級，所有節點必須在預設根節點底下");
      return;
    }

    // 檢查目標節點是否存在
    const overNodeInfo = findNodeWithParent(treeData, over.id);
    if (!overNodeInfo) {
      console.log("Over node not found");
      return;
    }

    // 防止拖拽到自己的子節點
    if (isDescendant(activeNodeInfo.node, over.id)) {
      console.log("Cannot drop on descendant");
      return;
    }

    // 簡化處理：移除節點後直接添加為目標節點的子節點
    let newTreeData = removeNodeByPath(treeData, activeNodeInfo.path);
    newTreeData = addToChildren(newTreeData, over.id, activeNodeInfo.node);

    console.log("Final tree data:", newTreeData);
    setTreeData(newTreeData);
    setIsLocalChange(true); // 🔑 標記為本地變更
  };
  // 獲取設備列表
  const handleFetch = () => {
    dispatch(fetchDeviceList());
  };

  const iconMapping = {
    bolt: BoltIcon,
    water: WaterIcon,
    setting: SettingIcon,
    bag: BagIcon,
  };

  // 設定感應器 - 只支持拖拽到節點上，不支持排序
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 15, // 增加距离
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300, // 增加延迟
        tolerance: 10,
      },
    })
  );

  // 深度複製函數
  const deepClone = (obj) => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj);
    if (Array.isArray(obj)) return obj.map(deepClone);

    const cloned = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  };

  // 🔑 檢查節點是否為第一層（根層級）
  const isFirstLevel = (nodeId) => {
    return treeData.some((node) => node.id === nodeId);
  };

  // 🔑 修复：找到节点及其完整路径（包括父节点信息）
  const findNodeWithParent = (
    nodes,
    targetId,
    parentPath = [],
    parentNode = null
  ) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const currentPath = [...parentPath, i];

      if (node.id === targetId) {
        return {
          node: deepClone(node),
          path: currentPath,
          parent: parentNode,
          parentPath: parentPath,
        };
      }

      if (node.children && node.children.length > 0) {
        const result = findNodeWithParent(
          node.children,
          targetId,
          [...currentPath, "children"],
          node
        );
        if (result) return result;
      }
    }
    return null;
  };

  // 找到節點及其路徑的函數（保持原有功能）
  const findNodePath = (nodes, targetId, path = []) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const currentPath = [...path, i];

      if (node.id === targetId) {
        return { node: deepClone(node), path: currentPath };
      }

      if (node.children) {
        const result = findNodePath(node.children, targetId, [
          ...currentPath,
          "children",
        ]);
        if (result) return result;
      }
    }
    return null;
  };

  // 移除節點的函數
  const removeNodeByPath = (data, path) => {
    const newData = deepClone(data);
    let current = newData;

    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }

    current.splice(path[path.length - 1], 1);
    return newData;
  };

  // 添加到子節點的函數
  const addToChildren = (data, parentId, node) => {
    const newData = deepClone(data);

    const updateNode = (nodes) => {
      return nodes.map((n) => {
        if (n.id === parentId) {
          return {
            ...n,
            children: [...(n.children || []), node],
          };
        }
        if (n.children) {
          return { ...n, children: updateNode(n.children) };
        }
        return n;
      });
    };

    return updateNode(newData);
  };

  // 檢查是否為後代節點
  const isDescendant = (parentNode, childId) => {
    if (!parentNode.children) return false;

    const checkChildren = (children) => {
      for (let child of children) {
        if (child.id === childId) return true;
        if (child.children && checkChildren(child.children)) return true;
      }
      return false;
    };

    return checkChildren(parentNode.children);
  };

  // 創建新節點的函數
  const createNewNode = (deviceType) => {
    const newId = Date.now().toString();
    const newNode = {
      id: newId,
      name: deviceType.note,
      category_id: deviceType.category_id,
      children: [],
    };

    // 只有設備類型才有詳細欄位
    if (currentMode === 1) {
      newNode.fields = {
        architecture: "建築位置:",
        endDevice: "終端設備名稱:",
        voltage: "電壓V:",
        ampere: "斷路器規格A:",
        phases: "相數:",
      };
    } else if (currentMode === 2) {
      newNode.fields = {
        architecture: "建築位置:",
        endDevice: "終端設備名稱:",
      };
    }

    return newNode;
  };

  // 添加子節點（顯示選擇器）
  const addChild = (parentId) => {
    setPendingParentId(parentId);
    setShowDeviceSelector(true);
  };

  // 關閉設備選擇器
  const handleDeviceSelectorClose = () => {
    setShowDeviceSelector(false);
    setPendingParentId(null);
  };

  // 開啟編輯彈窗
  const handleEditNode = (node) => {
    setEditingNode(node);
    setShowEditModal(true);
  };

  // 關閉編輯彈窗
  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingNode(null);
  };

  // 🔑 修復 - 確保 id 轉為字串後再使用 startsWith
  const isDeviceTypeId = (id) => {
    return String(id).startsWith("device-type-");
  };

  // 處理拖曳開始
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // 🔑 檢查是否嘗試拖拽第一層節點 - 先轉為字串比較
    if (isFirstLevel(active.id)) {
      console.warn("第一層節點無法被拖拽");
      return;
    }

    // 🔑 修復 - 使用輔助函數檢查是否為設備類型拖拽
    if (isDeviceTypeId(active.id)) {
      const deviceType = active.data.current?.deviceType;
      if (deviceType) {
        setDraggedNode(createNewNode(deviceType));
      }
    } else {
      // 普通節點拖拽
      const nodeInfo = findNodePath(treeData, active.id);
      if (nodeInfo) {
        setDraggedNode(nodeInfo.node);
      }
    }
  };

  // 處理拖曳懸停
  const handleDragOver = (event) => {
    const { over } = event;
    setOverId(over?.id || null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {/* 頭部 */}
      <Panel>
        <div className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold mb-2">
                迴路樹狀結構管理
              </h1>
              <p className="text-sm sm:text-base text-gray-300">
                使用樹狀結構直觀管理電路階層、點擊+按鈕新增子節點
              </p>
            </div>
            {/* 切換按鈕 */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => dispatch(setCurrentMode(1))}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  currentMode === 1
                    ? "bg-yellow-600 text-white border-yellow-500"
                    : "bg-transparent text-gray-300 border-gray-500 hover:bg-gray-600/20"
                }`}
              >
                用電
              </button>
              <button
                onClick={() => dispatch(setCurrentMode(2))}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  currentMode === 2
                    ? "bg-blue-600 text-white border-blue-500"
                    : "bg-transparent text-gray-300 border-gray-500 hover:bg-gray-600/20"
                }`}
              >
                用水
              </button>
            </div>
          </div>

          {/* 節點類型說明 */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
              {currentCategory.map((deviceType) => {
                const Icon = iconMapping[deviceType.icon];
                return (
                  <div
                    key={`device_type_${deviceType.category_id}`}
                    className={`
                    flex items-center gap-2 p-2 sm:p-3 rounded-lg border-2 bg-[#1A111DB3]
                    
                    hover:shadow-md transition-all duration-200
                  `}
                    style={{ borderColor: deviceType.color || "#6B7280" }}
                  >
                    <div className="text-xs sm:text-sm font-medium truncate">
                      {deviceType.category_name}
                    </div>
                    <div className="text-xs text-[#CCBBBB] truncate border-l-2 pl-1 border-[#CCBBBB]">
                      {deviceType.note}
                    </div>
                    <div className="flex items-center justify-center w-8 h-8 p-1 rounded-full bg-[#6D6A6A2A]">
                      <Icon />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Panel>

      <Panel>
        {/* 樹狀結構內容 */}
        <div className="w-full">
          <div className="bg-black/20 rounded-lg p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                迴路階層結構
                <Menu size={20} />
              </h2>
            </div>

            {/* 🔑 移除 SortableContext 的排序策略，改用簡單的拖拽到節點上 */}
            <div className="space-y-2 min-h-[60px] w-full overflow-auto no-scrollbar">
              {treeData.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <div className="text-lg font-semibold mb-2">暫無節點</div>
                  <div className="text-sm">
                    拖拽節點到現有節點或使用 + 按鈕新增
                  </div>
                </div>
              ) : (
                treeData.map((node) => (
                  <SortableTreeNode
                    key={`tree_data_${node.id}`}
                    node={node}
                    level={0}
                    currentCategory={currentCategory}
                    onToggleExpand={toggleExpand}
                    isNodeExpanded={isNodeExpanded}
                    onAddChild={addChild}
                    onDeleteNode={deleteNode}
                    onEditNode={handleEditNode}
                    activeId={activeId}
                    overId={overId}
                    isRoot={true} // 第一層節點都標記為 root
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </Panel>

      {/* 拖拽預覽 */}
      <DragOverlay>
        {activeId ? <DragPreview node={draggedNode} /> : null}
      </DragOverlay>

      {/* 設備類型選擇器 */}
      {showDeviceSelector && (
        <DeviceTypeSelector
          iconMapping={iconMapping}
          deviceTypes={currentCategory}
          onSelect={handleDeviceTypeSelect}
          onClose={handleDeviceSelectorClose}
        />
      )}

      {/* 編輯設備彈窗 */}
      {showEditModal && editingNode && (
        <EditDeviceModal
          currentMode={currentMode}
          node={editingNode}
          onSave={handleSaveEdit}
          onClose={handleCloseEdit}
        />
      )}
    </DndContext>
  );
}
